import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Calendar, User, ArrowLeft } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BlogPost = () => {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBlog();
  }, [slug]);

  const fetchBlog = async () => {
    try {
      const response = await axios.get(`${API}/blogs/${slug}`);
      setBlog(response.data);
    } catch (error) {
      console.error('Error fetching blog:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white py-24">
        <div className="container mx-auto px-6 md:px-12 max-w-4xl text-center">
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="min-h-screen bg-white py-24">
        <div className="container mx-auto px-6 md:px-12 max-w-4xl text-center">
          <p className="text-slate-600 mb-6">Blog post not found.</p>
          <Link to="/blog" className="text-[#0B1B32] font-medium hover:underline">
            Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Breadcrumb */}
      <div className="bg-[#F8FAFC] py-6">
        <div className="container mx-auto px-6 md:px-12 max-w-4xl">
          <Link to="/blog" className="inline-flex items-center gap-2 text-[#0B1B32] hover:gap-3 transition-all" data-testid="back-to-blog">
            <ArrowLeft size={18} /> Back to Blog
          </Link>
        </div>
      </div>

      {/* Blog Header */}
      <section className="py-16">
        <div className="container mx-auto px-6 md:px-12 max-w-4xl">
          <div className="mb-6">
            <span className="inline-block bg-[#0B1B32] text-white px-4 py-1.5 text-sm font-medium">
              {blog.category}
            </span>
          </div>
          <h1 className="text-4xl md:text-5xl font-medium tracking-tight text-[#0B1B32] mb-6" data-testid="blog-post-title">
            {blog.title}
          </h1>
          <div className="flex items-center gap-6 text-slate-600">
            <span className="flex items-center gap-2">
              <User size={18} />
              {blog.author}
            </span>
            <span className="flex items-center gap-2">
              <Calendar size={18} />
              {formatDate(blog.published_at)}
            </span>
          </div>
        </div>
      </section>

      {/* Featured Image */}
      <section className="pb-16">
        <div className="container mx-auto px-6 md:px-12 max-w-4xl">
          <img 
            src={blog.image_url} 
            alt={blog.title} 
            className="w-full h-[400px] object-cover shadow-2xl"
          />
        </div>
      </section>

      {/* Blog Content */}
      <section className="pb-24">
        <div className="container mx-auto px-6 md:px-12 max-w-4xl">
          <div className="prose prose-lg max-w-none">
            <p className="text-xl text-slate-700 leading-relaxed mb-8">
              {blog.excerpt}
            </p>
            <div className="text-slate-600 leading-relaxed whitespace-pre-line">
              {blog.content}
            </div>
          </div>
        </div>
      </section>

      {/* Back to Blog CTA */}
      <section className="py-16 bg-[#F8FAFC]">
        <div className="container mx-auto px-6 md:px-12 max-w-4xl text-center">
          <Link
            to="/blog"
            className="inline-block bg-[#0B1B32] text-white hover:bg-[#1e293b] rounded-none px-8 py-3 font-medium transition-all duration-300 text-sm tracking-wide shadow-lg hover:shadow-xl hover:-translate-y-1"
          >
            View All Articles
          </Link>
        </div>
      </section>
    </div>
  );
};

export default BlogPost;